import { useState } from "react";
import {
  useAdminBackfillEventImages, useAdminCategoryImages, useAdminGeocodeBackfill,
  useAdminSources, useRunScrape, useToggleSource, type AdminSource,
} from "../../lib/queries";

function relTime(ts: number | null): string {
  if (!ts) return "never";
  const now = Math.floor(Date.now() / 1000);
  const d = now - ts;
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export function AdminSources() {
  const { data: sources = [], isPending } = useAdminSources();
  const toggleSource = useToggleSource();
  const runScrape = useRunScrape();
  const [runMsg, setRunMsg] = useState<string | null>(null);

  return (
    <div style={{ padding: "16px 14px 32px" }}>
      <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 10 }}>
        scrape sources · cron runs every 6h · `0 */6 * * *` UTC
      </div>

      <div style={{
        marginBottom: 14,
        display: "flex", gap: 8, flexWrap: "wrap",
      }}>
        <button
          onClick={async () => {
            setRunMsg("running all…");
            try {
              await runScrape.mutateAsync({ limit: 100 });
              setRunMsg("done — refresh to see counts");
            } catch (e) { setRunMsg(`error: ${String(e).slice(0, 60)}`); }
          }}
          disabled={runScrape.isPending}
          style={primaryBtn}
        >
          Run all sources now
        </button>
        {runMsg && (
          <span className="ma-mono" style={{ fontSize: 10, color: "var(--ma-ink-soft)", alignSelf: "center", letterSpacing: "0.1em" }}>
            {runMsg}
          </span>
        )}
      </div>

      {isPending && (
        <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 24 }}>
          loading sources…
        </div>
      )}

      <div style={{
        display: "grid",
        // Auto-fit to 2 / 3 columns once each card has 360px headroom.
        // On mobile we fall back to a single column naturally.
        gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
        gap: 10,
      }}>
        {sources.map((s) => (
          <SourceCard
            key={s.id}
            source={s}
            onToggle={() => toggleSource.mutate({ id: s.id, enabled: !s.enabled })}
            onRun={() => runScrape.mutate({ source: s.id, limit: 100, force: !s.enabled })}
            running={runScrape.isPending}
          />
        ))}
      </div>

      <ToolStrip />
    </div>
  );
}

function SourceCard({
  source, onToggle, onRun, running,
}: { source: AdminSource; onToggle: () => void; onRun: () => void; running: boolean }) {
  return (
    <div style={{
      background: "var(--ma-cream)",
      border: "var(--ma-border) solid var(--ma-ink)",
      borderRadius: "var(--ma-card-radius)",
      padding: "12px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span className="ma-display" style={{ fontSize: 18, color: "var(--ma-ink)" }}>
              {source.id}
            </span>
            <span className="ma-mono" style={{
              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
              padding: "2px 6px",
              background: source.enabled ? "var(--ma-canal)" : "var(--ma-ink-soft)",
              color: "var(--ma-paper)",
              borderRadius: 2,
            }}>
              {source.enabled ? "enabled" : "disabled"}
            </span>
          </div>
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)" }}>
            {source.label}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={onToggle} style={secondaryBtn}>
            {source.enabled ? "disable" : "enable"}
          </button>
          <button onClick={onRun} disabled={running} style={primaryBtn}>
            run now
          </button>
        </div>
      </div>

      <div style={{
        marginTop: 10, paddingTop: 8,
        borderTop: "1px dashed var(--ma-line)",
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8,
      }}>
        <Stat label="last run" value={relTime(source.lastRunAt)} />
        <Stat label="last completed" value={relTime(source.lastCompletedAt)} />
        <Stat label="imported" value={String(source.totalImported)} />
      </div>

      {source.lastError && (
        <div className="ma-mono" style={{
          marginTop: 8, fontSize: 10, color: "var(--ma-brick)", letterSpacing: "0.04em",
          background: "var(--ma-paper)", padding: "6px 8px", borderRadius: 4,
          whiteSpace: "pre-wrap", overflow: "hidden", maxHeight: 80,
        }}>
          {source.lastError}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ma-ink-soft)" }}>
        {label}
      </div>
      <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink)", marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function ToolStrip() {
  const catImages = useAdminCategoryImages();
  const backfillImages = useAdminBackfillEventImages();
  const geocode = useAdminGeocodeBackfill();
  const [msg, setMsg] = useState<string | null>(null);

  return (
    <div style={{ marginTop: 22 }}>
      <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 8 }}>
        one-shot tools
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ToolButton
          label="Pre-warm category images (10 calls)"
          onClick={async () => {
            setMsg("generating…");
            try { await catImages.mutateAsync(); setMsg("done"); }
            catch (e) { setMsg(`error: ${String(e).slice(0, 60)}`); }
          }}
          running={catImages.isPending}
        />
        <ToolButton
          label="Backfill missing event images (10 events)"
          onClick={async () => {
            setMsg("generating…");
            try {
              const r = await backfillImages.mutateAsync({ limit: 10 });
              setMsg(`updated ${r.updated} / ${r.attempted}`);
            } catch (e) { setMsg(`error: ${String(e).slice(0, 60)}`); }
          }}
          running={backfillImages.isPending}
        />
        <ToolButton
          label="Geocode missing lat/lng (10 rows)"
          onClick={async () => {
            setMsg("geocoding…");
            try {
              const r = await geocode.mutateAsync({ kind: "all", limit: 10 });
              setMsg(`venues:${r.result.venues} events:${r.result.events} errs:${r.result.errors}`);
            } catch (e) { setMsg(`error: ${String(e).slice(0, 60)}`); }
          }}
          running={geocode.isPending}
        />
        {msg && (
          <div className="ma-mono" style={{ fontSize: 10, color: "var(--ma-ink-soft)", letterSpacing: "0.1em", marginTop: 4 }}>
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolButton({ label, onClick, running }: { label: string; onClick: () => void; running: boolean }) {
  return (
    <button onClick={onClick} disabled={running} style={{
      ...secondaryBtn,
      padding: "10px 14px", textAlign: "left",
      fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.08em",
      opacity: running ? 0.5 : 1,
    }}>
      {running ? "running…" : label}
    </button>
  );
}

const primaryBtn: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--ma-ink)", color: "var(--ma-paper)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = {
  padding: "8px 12px",
  background: "transparent", color: "var(--ma-ink)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
